import React, { useState } from "react";

import classnames from "classnames";
import camelCase from "lodash/camelCase";
import isEmpty from "lodash/isEmpty";
import mapKeys from "lodash/mapKeys";
import PropTypes from "prop-types";

import { useTranslation } from "components/i18n/source";
import { useClientInfo } from "components/instrumentation/source";
import { useBootstrap } from "components/store/source";
import { Button, Checkbox, Form, Input, Text } from "components/ui/source";

import { IconEye, IconEyeStrike } from "icons";

import AccountsApi from "../../shared/api/AccountsApi";
import { GoogleRecaptcha } from "../GoogleRecaptcha/GoogleRecaptcha";

import { validateSignUpForm } from "./validator";

import styles from "./Accounts.module.scss";

function SignUpEmailContainer({ setLoading, email, logSubmit }) {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const { inEu } = useClientInfo();
  const { hideMarketingOnSignup } = useBootstrap();

  const prefix = "modals.accounts.signUp.email";

  // Clear error on input focused
  function onInputTouched() {
    setError(null);
  }

  async function onSubmitEmail(account, { setErrors }, captchaToken) {
    // As passwordConfirmation is removed from simplyfing, put data
    account.passwordConfirmation = account.password;

    setLoading(true);

    // Send account creation request
    AccountsApi.create(account, { captchaToken })
      .then(async (response) => {
        if (response.error) {
          setLoading(false);
          setError(response.error);
        } else if (response.errors) {
          setLoading(false);

          // Mark error
          const errors = response.errors;
          if (Object.keys(errors).length) {
            setErrors(mapKeys(errors, (_, key) => camelCase(key)));
          } else {
            setError(t("modals.accounts.errors.etc"));
          }
        } else {
          await logSubmit("email_signup", account.email);
          window.location.reload();
        }
      })
      .catch((e) => {
        setLoading(false);
        setError(t("modals.accounts.errors.etc"));
      });
  }

  const passwordHide = (
    <div
      className={styles.eyeStrikeContainer}
      onClick={() => setShowPassword(false)}
    >
      <IconEyeStrike size="lg" />
    </div>
  );
  const passwordShow = (
    <div
      className={styles.eyeIconContainer}
      onClick={() => setShowPassword(true)}
    >
      <IconEye size="lg" />
    </div>
  );
  const passwordRequirements = (
    <>
      <Text types={["regular", "sm", "grey-2"]}>
        {t("accounts.passwordRequirements.description")}
      </Text>
      <Text types={["regular", "sm", "grey-2"]}>
        {t("accounts.passwordRequirements.req1")}
      </Text>
      <Text types={["regular", "sm", "grey-2"]}>
        {t("accounts.passwordRequirements.req2")}
      </Text>
      <Text types={["regular", "sm", "grey-2"]}>
        {t("accounts.passwordRequirements.req3")}
      </Text>
      <Text types={["regular", "sm", "grey-2"]}>
        {t("accounts.passwordRequirements.req4")}
      </Text>
    </>
  );

  return (
    <GoogleRecaptcha>
      {({ getToken }) => (
        <>
          <Text
            className={classnames(styles.header, styles.centered_text)}
            types={["h4", "medium"]}
            translateKey={`${prefix}.title`}
          />

          <Text
            className={classnames(
              styles.error_message,
              styles.centered_text,
              error && styles.visible,
            )}
            types={["subtitles", "accent-red"]}
          >
            {error}
          </Text>

          <Form
            className={styles.form_container}
            validate={values => validateSignUpForm(values, t)}
            initialValues={{
              email: email,
              marketingOptIn: !inEu && !hideMarketingOnSignup,
              discountPricingMember: true,
            }}
            initialErrors={{ email: "" }}
            onSubmit={(values, formik) => {
              getToken("signup").then(captchaToken => {
                onSubmitEmail(values, formik, captchaToken);
              });
            }}
            validateOnChange={true}
          >
            {({ errors }) => (
              <>
                <Text
                  className={styles.subheader}
                  types={["body", "medium"]}
                  translateKey={`${prefix}.form.accountInfo`}
                />

                <Input
                  placeholder={t("modals.accounts.email.email")}
                  type="email"
                  name="email"
                  theme="popup"
                  onTouched={onInputTouched}
                  required
                  disabled
                  readOnly
                />
                <Input
                  placeholder={t("modals.accounts.email.confirmEmail")}
                  type="email"
                  name="emailConfirmation"
                  theme="popup"
                  onTouched={onInputTouched}
                  required
                />
                <Input
                  placeholder={t("modals.accounts.email.password")}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  theme="popup"
                  onTouched={onInputTouched}
                  helpText={passwordRequirements}
                  inputIcon={showPassword ? passwordHide : passwordShow}
                  required
                />
                <Input
                  placeholder={t("modals.accounts.email.firstName")}
                  name="firstName"
                  theme="popup"
                  onTouched={onInputTouched}
                  required
                />
                <Input
                  placeholder={t("modals.accounts.email.lastName")}
                  name="lastName"
                  theme="popup"
                  onTouched={onInputTouched}
                  required
                />

                {!hideMarketingOnSignup && (
                  <Checkbox
                    label={t(`${prefix}.form.marketingOptIn`)}
                    name="marketingOptIn"
                  />
                )}

                <Button
                  upperCase={false}
                  disabled={!isEmpty(errors)}
                  htmlType="submit"
                >
                  <Text
                    translateKey={`${prefix}.form.cta`}
                    types={["subtitle"]}
                  />
                </Button>
              </>
            )}
          </Form>
        </>
      )}
    </GoogleRecaptcha>
  );
}

SignUpEmailContainer.propTypes = {
  setLoading: PropTypes.func,
  logSubmit: PropTypes.func.isRequired,
  email: PropTypes.string,
};

SignUpEmailContainer.defaultProps = {
  setLoading: () => { },
  logSubmit: async () => { },
  email: null,
};

export default SignUpEmailContainer;
